import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getLogger } from 'loglevel';
import { useCallback } from 'react';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { LoadingOverlay } from '@apple/ui/loading-overlay';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { deleteProductCategory, saveProductCategory } from '@apple/features/product/api/category';
import { CategoryForm } from '@apple/features/product/components/category-form';
import { categoryQueries, categoryQueryKeys } from '@apple/features/product/queries/category';
import type { ProjectId } from '@apple/features/project/models';

const log = getLogger('manage-category');

export const Route = createFileRoute('/_authed/_admin/manage/categories_/$categoryId')({
	parseParams: ({ categoryId }) => ({
		categoryId: Number(categoryId),
	}),
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductCategoryManagement.Write',
		});
	},
	component: EditCategoryRoute,
});

function EditCategoryRoute() {
	const { queryClient } = Route.useRouteContext();
	const goBackToList = useBackOrNavigate();
	const { categoryId } = Route.useParams();
	const { t } = useTranslation('manage');
	const categoryQuery = useQuery(categoryQueries.getById(categoryId));

	const onSuccessfulChange = useCallback(
		async (message: string) => {
			await queryClient.invalidateQueries({
				queryKey: categoryQueryKeys.all,
			});
			notifications.show({
				message: message,
				icon: <Success />,
				color: 'green',
				autoClose: 5000,
			});
			await goBackToList('/manage/categories');
		},
		[goBackToList, queryClient],
	);

	const onFailedChange = useCallback(
		(error: Error) => {
			//TODO: Have the API return model validation errors
			if (!(error instanceof ServerValidationError)) {
				log.error(error);
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
		[t],
	);

	const saveCategoryMutation = useMutation({
		mutationFn: saveProductCategory,
		onSuccess: async () => {
			await onSuccessfulChange(t('categories.edit.success'));
		},
		onError: onFailedChange,
	});

	const deleteCategoryMutation = useMutation({
		mutationFn: deleteProductCategory,
		onSuccess: async () => {
			await onSuccessfulChange(t('categories.delete.success'));
		},
		onError: onFailedChange,
	});

	const handleDeleteCategory = useCallback(
		(projectId: ProjectId) => {
			modals.openConfirmModal({
				title: t('categories.delete.title'),
				labels: {
					confirm: t('common:buttons.confirm'),
					cancel: t('common:buttons.cancel'),
				},
				onConfirm: () => deleteCategoryMutation.mutate(projectId),
				children: <Text ta='center'>{t('categories.delete.message')}</Text>,
			});
		},
		[deleteCategoryMutation, t],
	);

	return (
		<DetailsLayout
			title={t('categories.edit.title', { categoryId: categoryQuery.data?.categoryId })}
		>
			{!categoryQuery.data ? (
				<LoadingOverlay visible />
			) : (
				<CategoryForm
					category={categoryQuery.data}
					onSave={saveCategoryMutation.mutateAsync}
					onDelete={handleDeleteCategory}
				/>
			)}
		</DetailsLayout>
	);
}
